import { AsteriskNotification } from "Components/AsteriskNotification";
import { BulmaNavButtons } from "Components/BulmaNavButtons";
import { useDocumentTitle } from "Components/Hooks";
import { Horizontal } from "Components/Horizontal";
import { RadioYesNoBool } from "Inputs/Radio";
import { Textarea } from "Inputs/Textarea";
import { Program, UnderwritingStrings } from "SharedConstants";
import { FitnessInstructorUnderwritingInformationModel } from "SharedConstants/Schema";
import { Heading } from "../../Components/Heading";
import { MVCForm, MVCFormServerProps } from "../MVCForm";
import { ModelStateValues } from "../Tools";

export function UnderwritingInformation(
  props: FitnessInstructorUnderwritingInformationModel & {
    formProps: MVCFormServerProps;
  },
) {
  const getInitialValues = (values: ModelStateValues) => {
    return {
      ...values,
      WantsStudioCoverageInformation: "false",
    };
  };

  const title = "Underwriting Information";
  useDocumentTitle(`${title} - ${props.TitleProps.PageTitleSuffix}`);
  return (
    <>
      <Heading {...props} title={title} />
      <MVCForm
        {...props.formProps}
        initialValues={getInitialValues(props.formProps.initialValues)}
      >
        {({ values, isSubmitting }) => (
          <div className="narrowFieldBodies3">
            <AsteriskNotification />
            <WaiverRequirementInput {...props} />
            <Horizontal isRequired label="Past Coverage" shouldNotPadLabel>
              <RadioYesNoBool fieldName="HasPastCoverageCancellation">
                <div>
                  {UnderwritingStrings.HasPastCoverageCancellationQuestion}
                </div>
              </RadioYesNoBool>
            </Horizontal>
            {values.HasPastCoverageCancellation == "true" && (
              <Horizontal
                isRequired
                label="Cancellation Details"
                shouldNotPadLabel
              >
                <div style={{ width: "100%" }}>
                  {UnderwritingStrings.CancellationDescriptionLabel}
                  <Textarea fieldName="CancellationDescription" />
                </div>
              </Horizontal>
            )}
            <Horizontal isRequired label="Current Insurer" shouldNotPadLabel>
              <RadioYesNoBool fieldName="HasNonRenewingCurrentInsurer">
                <div>
                  {UnderwritingStrings.HasNonRenewingCurrentInsurerQuestion}
                </div>
              </RadioYesNoBool>
            </Horizontal>
            <Horizontal isRequired label="Recent Claim" shouldNotPadLabel>
              <RadioYesNoBool fieldName="HasInsurerPaidRecentLiabilityClaim">
                <div>
                  {
                    UnderwritingStrings.HasInsurerPaidRecentLiabilityClaimQuestion
                  }
                </div>
              </RadioYesNoBool>
            </Horizontal>
            {values.HasInsurerPaidRecentLiabilityClaim == "true" && (
              <Horizontal isRequired label="Claim Details" shouldNotPadLabel>
                <div style={{ width: "100%" }}>
                  {UnderwritingStrings.RecentLiabilityClaimDescriptionLabel}
                  <Textarea fieldName="RecentLiabilityClaimDescription" />
                </div>
              </Horizontal>
            )}
            <Horizontal isRequired label="Risk Management" shouldNotPadLabel>
              <RadioYesNoBool fieldName="HasRiskManagementPlan">
                <div>
                  Do you have a written safety plan or risk management plan in
                  place?{" "}
                  <em>
                    (For example: Emergency Procedures, Written/Posted Rules
                    and/or Regulations, Code of Conduct, Consent Form, Emergency
                    Contact Form)
                  </em>
                </div>
              </RadioYesNoBool>
            </Horizontal>
            {props.ShowAerialActivitiesQuestion && (
              <Horizontal
                isRequired
                label="Aerial Activities"
                shouldNotPadLabel
              >
                <RadioYesNoBool
                  fieldName="HasAerialActivities"
                  isReadonly={props.IsAerialActivitiesQuestionReadonly}
                >
                  <div>
                    Do the students participate in any aerial activities?
                  </div>
                </RadioYesNoBool>
              </Horizontal>
            )}
            {props.ShowStudioCoverageQuestion && (
              <Horizontal isRequired label="Studio Coverage" shouldNotPadLabel>
                <RadioYesNoBool fieldName="WantsStudioCoverageInformation">
                  <div>
                    If you have your own personal trainer or yoga studio, would
                    you like to receive information regarding Studio Coverage?
                  </div>
                </RadioYesNoBool>
              </Horizontal>
            )}
            <BulmaNavButtons
              backCaption="Back"
              continueCaption="Continue"
              isDisabled={isSubmitting}
            />
          </div>
        )}
      </MVCForm>
    </>
  );
}

function WaiverRequirementInput(
  props: FitnessInstructorUnderwritingInformationModel,
) {
  const isSup = props.Program === Program.Sup;
  return (
    <Horizontal isRequired label="Waiver Requirement" shouldNotPadLabel>
      <RadioYesNoBool fieldName="HasAgreedToWaiver">
        <div>
          Each {isSup ? "SUP instructor" : "personal trainer"} must install a
          Release and Waiver of Liability and Indemnity Agreement for all
          clients. Unintentional error on your part in securing Waiver and
          Release forms shall not void your coverage in the event of an
          occurrence to a client. However, your failure to maintain an adequate
          system to regularly secure Waiver and Release forms shall void your
          coverage in the event of an occurrence to a client.
          {isSup ? (
            ""
          ) : (
            <>
              {" "}
              A full supply of Waiver and Release forms shall be shipped to you
              upon request.
            </>
          )}
          {props.SampleWaiverLink && (
            <div className="has-text-centered" style={{ marginTop: "1em" }}>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={props.SampleWaiverLink}
                className="button is-info"
              >
                Download Sample Waiver
              </a>
            </div>
          )}
          <div className="has-text-weight-bold" style={{ marginTop: "1em" }}>
            Do you agree to comply with this requirement?{" "}
          </div>
        </div>
      </RadioYesNoBool>
    </Horizontal>
  );
}
